import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';

import {Headline, P} from 'UI/components/layout/Text';
import {WaypointThumbnail} from 'UI/components/modules/WaypointThumbnail';

/** Opacity applied to the card when `dimmed` is set (e.g. For locked waypoints). */
const DIMMED_OPACITY = 0.6;

/**
 * Renders the waypoint's thumbnail and text block, shared between the linked and static variants
 * of the `WaypointCard` component.
 *
 * @param props                   The content props.
 * @param props.cornerIcon        Optional status badge overlapping the thumbnail's bottom-right edge.
 * @param props.description       Optional short description text.
 * @param props.dimmed            Whether the card should render muted.
 * @param props.distanceAriaLabel Optional accessible label for the distance badge.
 * @param props.distanceLabel     Optional short distance text shown next to the title.
 * @param props.imageAlt          Alt text for the waypoint image.
 * @param props.imageUrl          URL of the waypoint's main image.
 * @param props.number            The waypoint number shown in the thumbnail's badge.
 * @param props.testId            The test ID used for automation selectors.
 * @param props.title             The waypoint title.
 * @param props.walkTitle         Optional name of the walk this waypoint belongs to.
 * @returns A JSX element rendering the waypoint's thumbnail and text block.
 */
const renderWaypointCardContent = ({
    cornerIcon,
    description,
    dimmed,
    distanceAriaLabel,
    distanceLabel,
    imageAlt,
    imageUrl,
    number,
    testId,
    title,
    walkTitle
}: Omit<ComponentProps, 'href'>) => (
    <Item $dimmed={dimmed ?? false} data-cy={`${testId}-item`}>
        <WaypointThumbnail
            cornerIcon={cornerIcon}
            dimmed={dimmed}
            imageAlt={imageAlt}
            imageUrl={imageUrl}
            number={number}
        />
        <TextBlock>
            {walkTitle && <WalkLabel data-cy={`${testId}-walk`}>{walkTitle}</WalkLabel>}
            <TitleRow>
                <ItemTitle $size="title" as="h3">{title}</ItemTitle>
                {distanceLabel && (
                    <DistanceBadge aria-label={distanceAriaLabel ?? distanceLabel} data-cy={`${testId}-distance`}>
                        <span aria-hidden="true">{distanceLabel}</span>
                    </DistanceBadge>
                )}
            </TitleRow>
            {description && (
                <ItemDescription $size="bodySmall">{description}</ItemDescription>
            )}
        </TextBlock>
    </Item>
);

/**
 * The props for the `WaypointCard` component.
 * Configures a single waypoint row: thumbnail, title, description and optional distance/status badges.
 */
interface ComponentProps {
    /** Small badge rendered overlapping the bottom-right of the thumbnail (e.g. Lock or state icon). */
    cornerIcon?: ReactNode;
    /** The waypoint's short description. */
    description?: string;
    /** Whether the card should render muted (e.g. For locked waypoints). */
    dimmed?: boolean;
    /** Accessible label for the distance badge, read out in full by screen readers (e.g. "850 m entfernt"). */
    distanceAriaLabel?: string;
    /** Short, visible distance text shown next to the title (e.g. "850 m"). */
    distanceLabel?: string;
    /** Link URL. When omitted the card renders as a non-interactive block. */
    href?: string;
    /** Alt text for the waypoint's main image. */
    imageAlt?: string;
    /** URL of the waypoint's main image. */
    imageUrl?: string;
    /** The waypoint number shown in the thumbnail's badge. */
    number: number;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'WaypointCard'
     */
    testId?: string;
    /** The waypoint title. */
    title: string;
    /** Name of the walk this waypoint belongs to, shown as a small label above the title. */
    walkTitle?: string;
}

/**
 * A single waypoint row combining a `WaypointThumbnail` with a title/description text block,
 * an optional distance badge, and an optional link wrapper. Shared between `WaypointPreviewList`
 * (walk detail page) and any component listing waypoints from different walks (e.g. Nearby waypoints)..
 *
 * @param props                   The component props.
 * @param props.cornerIcon        Optional status badge overlapping the thumbnail's bottom-right edge.
 * @param props.description       Optional short description text.
 * @param props.dimmed            Whether the card should render muted.
 * @param props.distanceAriaLabel Optional accessible label for the distance badge.
 * @param props.distanceLabel     Optional short distance text shown next to the title.
 * @param props.href              Optional link URL.
 * @param props.imageAlt          Alt text for the waypoint image.
 * @param props.imageUrl          URL of the waypoint's main image.
 * @param props.number            The waypoint number shown in the thumbnail's badge.
 * @param props.testId            An optional test ID used for automation selectors. Defaults to `'WaypointCard'`.
 * @param props.title             The waypoint title.
 * @param props.walkTitle         Optional name of the walk this waypoint belongs to.
 * @returns A JSX element rendering the WaypointCard.
 *
 * @example
 * ```tsx
 * <WaypointCard title="The Whispering Courtyard" number={1} imageUrl="/media/stop-1.jpg" distanceLabel="850 m" />
 * ```
 */
const WaypointCard = ({
    cornerIcon,
    description,
    dimmed = false,
    distanceAriaLabel,
    distanceLabel,
    href,
    imageAlt,
    imageUrl,
    number,
    testId = 'WaypointCard',
    title,
    walkTitle
}: ComponentProps) => (
    href
        ? (
            <ItemLink href={href}>
                {renderWaypointCardContent({
                    cornerIcon,
                    description,
                    dimmed,
                    distanceAriaLabel,
                    distanceLabel,
                    imageAlt,
                    imageUrl,
                    number,
                    testId,
                    title,
                    walkTitle
                })}
            </ItemLink>
        )
        : renderWaypointCardContent({
            cornerIcon,
            description,
            dimmed,
            distanceAriaLabel,
            distanceLabel,
            imageAlt,
            imageUrl,
            number,
            testId,
            title,
            walkTitle
        })
);

WaypointCard.displayName = 'WaypointCard';

export {WaypointCard};

// ── Styled ──────────────────────────────────────────────────

const Item = styled.div<{$dimmed: boolean}>`
    align-items: center;
    background: ${({$dimmed, theme}) => ($dimmed ? theme.colors.surfaceMid : theme.colors.surfaceHigh)};
    border-radius: ${spacing(5)};
    display: flex;
    gap: ${spacing(4)};
    opacity: ${({$dimmed}) => ($dimmed ? DIMMED_OPACITY : 1)};
    padding: ${spacing(5)};
`;

const TextBlock = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${spacing(1)};
    min-width: 0;
`;

const WalkLabel = styled.span`
    color: ${({theme}) => theme.colors.accentDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
`;

const TitleRow = styled.div`
    align-items: baseline;
    display: flex;
    gap: ${spacing(2)};
    justify-content: space-between;
`;

// Caps very long titles at two lines instead of letting the card grow unbounded.
const ItemTitle = styled(Headline)`
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    margin: 0;
    overflow: hidden;
`;

const DistanceBadge = styled.span`
    color: ${({theme}) => theme.colors.inkSecondary};
    flex-shrink: 0;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 700;
    white-space: nowrap;
`;

// Caps very long descriptions at two lines instead of letting the card grow unbounded.
const ItemDescription = styled(P)`
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    color: ${({theme}) => theme.colors.inkSecondary};
    display: -webkit-box;
    overflow: hidden;
`;

const ItemLink = styled(Link)`
    color: inherit;
    display: block;
    text-decoration: none;
`;